import { FormGroup, Switch } from "@blueprintjs/core";
import autobind from "autobind-decorator";
import React, { ReactNode } from "react";
import { Provider, Registry } from 'react-registry';
import styled from "styled-components";

import { LabelField } from "common/form/fields/label/LabelField";
import LayoutField from "common/form/fields/layout/LayoutField";
import BlockLayout from "common/form/fields/layout/layoutTypes/BlockLayout";
import FixedLayout from "common/form/fields/layout/layoutTypes/FixedLayout";
import FlexLayout from "common/form/fields/layout/layoutTypes/FlexLayout";
import GridLayout from "common/form/fields/layout/layoutTypes/GridLayout";
import ListOrderWidget from "common/form/widgets/listOrder/ListOrderWidget";
import { BlockView } from "common/views/block/BlockView.view";
import StringUtil from "core/util/StringUtil";
import { iDef, nDef } from "core/util/TypeUtils";
import { registerField } from "../../common/form/register";
import { IFragmentLayoutConfig } from "./FragmentLayout";
import { ILabelDefinition } from "./label/ILabel";
import LabelProvider from "./label/LabelProvider";

// tslint:disable:interface-name

export type FragmentRenderer<T> = React.SFC<T> | React.ComponentClass<T> | ReactNode | ILabelDefinition;

export interface IDataFragment<T> extends Fragment<T & {label?: ReactNode }> { // label prop to use the rendered label element in your fragment
	label: ReactNode | ILabelDefinition; // label does not accept props for now. it needs to be able to render when the fragment is disabled. ex: table header
	sort?: string;
	sortable?: boolean;
}

export interface Fragment<T> {
	name: string;
	component: FragmentRenderer<T>; // TODO rename to "render"?
	enabled?: (o: T) => boolean; // the render function will be called if this is true and disabled is not true
	disabled?: (o: T) => boolean; // the render function will be called if this is false and enabled is not false
	style?: ((T) => string) | string; // wrap in a styled-component with this css
	renderSkeleton?: FragmentRenderer<void>;
	valadition?:(o:string| boolean)=>boolean;
}

interface InputProvider {
	getInput: () => string;
}

export type FragmentList<T, F extends Fragment<T> = Fragment<T>> = Record<string, F>/* | Fragment<T>[]*/;
type ProviderWrapper<T> = React.SFC<IFragmentProviderProps<T>> & InputProvider;

// result component from queryOne or queryMany
interface IFragmentProviderProps<T> {
	fragments?: string | string[]; // all if undefined / null
	fragmentProps?: T;
	registry?: string;
	fragmentList?: FragmentList<T>;
	defaultFragments?: string[];
	separator?: JSX.Element;
	labels?: LabelProvider<any>;
}

export class FragmentProvider<T> extends React.Component<IFragmentProviderProps<T>> {
	private provider: Provider;

	public constructor(props: IFragmentProviderProps<T>) {
		super(props);
		this.provider = new Provider({registry: props.registry});
	}

	public renderFragment = (fId, index?, arr?) => {
		const { fragmentProps = {} as T, fragmentList } = this.props;

		const fragment = fragmentList ? fragmentList[fId] : this.provider.get(fId) as Fragment<T>;

		if(!fragment) {
			return null; // invalid fragment
		}

		// allow for a children prop for fragments just
		const children = (fragmentProps && fragmentProps['children']) || this.props.children;

		// render a label provided by IDataFragment if its available
		const label = this.props['label'] && this.createElement(this.props['label']);

		const props = {...fragmentProps, key: fId, children, label};

		// check if the fragment is enabled based on its props
		if(nDef(fragment.enabled) || fragment.enabled(fragmentProps)) {
			const element = this.createElement(fragment.component, props);

			if(fragment.style) {

				// if the style field is a function, pass the fragment props to get the string
				const style = typeof fragment.style === "function" ? fragment.style(props) : fragment.style;

				// TODO can we create the style component at register time instead?
				const Styled = styled.div`${style}`;

				return (
					<Styled key={fId}>
						{element}
					</Styled>
				);
			}

			return this.renderRow(element, index, arr);

		} else if(fragment.enabled && !fragment.enabled(fragmentProps) && fragment.renderSkeleton) {

			// fragment disabled and a skeleton render is present
			const element = this.createElement(fragment.renderSkeleton);

			return this.renderRow(element, index, arr);
		}

		return null;
	}


	private renderRow = (element: React.ReactNode, index, arr) => {
		const { separator } = this.props;

		if(iDef(separator) && arr.length - 1 !== index) {
			return (
				<React.Fragment key={index}>
					{element}
					{separator}
				</React.Fragment>
			);
		}

		return element;
	}

	@autobind
	public createElement(fragmentComponent: FragmentRenderer<T>, props?: T) {
		return FragmentProvider.createElement(this.props.registry, fragmentComponent, props);
	}

	// TODO no any
	public static createElement<T>(registry: string, fragmentComponent: FragmentRenderer<T>, props?: T) {
		// TODO create a label provider for all the labels at register time instead of at render time
		if(LabelProvider.isValidDefinition(fragmentComponent as any)) {
			const label = fragmentComponent as ILabelDefinition;
			return LabelProvider.createOne({k: StringUtil.format("%s.%s", registry, label.k), ...label});
		}

		return React.isValidElement(fragmentComponent) ? fragmentComponent : React.createElement(fragmentComponent as any, props);
	}

	public render() {
		// use default fragments if an active array is not specified
		const { defaultFragments, fragments = defaultFragments } = this.props;

		// list of active fragments not provided, render all of them
		if(!fragments) {
			return this.renderFragment(defaultFragments);
		}

		// render the list a list of or a single fragment
		return typeof fragments === "string" ? this.renderFragment(fragments) : fragments.map(this.renderFragment);
	}

	public static forFragments<T>(name: string, fragments: FragmentList<T>): React.SFC<IFragmentProviderProps<T>> {
		// const TypedProvider = FragmentProvider as new (props: IFragmentProviderProps<T>) => FragmentProvider<IFragmentProviderProps<T>>;

		FragmentProvider.registerFragments(name, fragments);
		const defaultFragments = Object.keys(fragments);

		return (props) => (
			<FragmentProvider {...props} registry={FragmentProvider.getRegistry(name)} defaultFragments={defaultFragments} />
		);
	}

	public static getRegistry(name: string): string {
		return StringUtil.format("fragments:%s", StringUtil.sanitize(name));
	}

	public static registerFragments<T>(name: string, fragments: FragmentList<T>): Array<Fragment<T>> {

		// check if fragments already registered
		// TODO

		Object.entries(fragments).forEach(([id, fragment]) => (
			Registry.register(fragment, {id, registry: FragmentProvider.getRegistry(name)}))
		);

		return Object.values(fragments);
	}

	public static getInput<T>(fragments: FragmentList<T>) {

		const options = Object.entries(fragments).map(([id, fragment]) => ({
			value: id,
			label: fragment.name
		}));

		return ListOrderWidget.registerWithOptions(options, true);
	}

	public static getLayoutInputComponent<T>(fragments: FragmentList<T>) {
		const options = Object.entries(fragments).map(([id, fragment]) => ({
			value: id,
			label: fragment.name
		}));

		const FragmentListWidget = ListOrderWidget.withOptions(options, true);

		return LayoutField.withOptions<IFragmentLayoutConfig>({
			renderRegionInput: (value, onChange) => {

				return (
					<React.Fragment>
						<FormGroup label="Wrapper">
							<Switch checked={value.wrapper} onChange={(e: any) => onChange({...value, wrapper: e.target.checked})} />
						</FormGroup>
						<FormGroup label="Separator">
							<Switch checked={value.seperator} onChange={(e: any) => onChange({...value, seperator: e.target.checked})} />
						</FormGroup>
						<FormGroup label="Default (external layout)">
							<Switch checked={value.default} onChange={(e: any) => onChange({...value, default: e.target.checked})} />
						</FormGroup>
						<FormGroup label="Enable Title">
							<Switch checked={value.enableTitle} onChange={(e: any) => onChange({...value, enableTitle: e.target.checked})} />
						</FormGroup>
						{
							value.enableTitle && (
								<LabelField
									formData={value.title}
									onChange={(e: any) => onChange({...value, title: e.title})}
									schema={{title: "Title"}}
									idSchema={undefined}
									uiSchema={undefined}
								/>
							)
						}
						<FormGroup label="Fragments">
							<FragmentListWidget
								onChange={(flist) => onChange({...value, fragments: flist})}
								value={value.fragments}
							/>
						</FormGroup>
					</React.Fragment>
				);
			},
			useDialog: true,
			layoutTypes: [BlockLayout, FlexLayout, GridLayout, FixedLayout]
		});
	}

	public static getLayoutInput<T>(fragments: FragmentList<T>) {
		return registerField(FragmentProvider.getLayoutInputComponent(fragments), "object");
	}
}



// WEBPACK FOOTER //
// ./src/core/components/FragmentProvider.tsx